import { Box, ButtonBase, IconButton, styled, Typography, type CSSObject } from '@mui/material';
import { colors } from '@rumblefish/ui';
import { Contact, Layout } from '@rumblefish/ui/Rumblefish23Theme';
import { Section } from '@rumblefish/ui/Rumblefish23Theme';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NextHeadWithMeta } from '../NextHeadWithMeta';
import { Articles } from './Articles';
import type { Article } from './Articles/Articles.types';
import { blogSeo } from './Blog.seo';
import type { BlogProps } from './Blog.types';
import { normalizeCategoryName } from './BlogPage.utils';

const IntroHeading = styled(Typography)(({ theme }) => ({
  ...(theme.mixins.neutralGradient as CSSObject),
  fontFamily: 'ClashDisplay',
  fontSize: '72px',
  fontWeight: 900,
  lineHeight: '1.1',
  letterSpacing: '-0.02em',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(3),

  [theme.breakpoints.down('md')]: {
    fontSize: '48px',
  },
}));

const IntroSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.invertedContent?.SECONDARY,
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: '1.6',
  maxWidth: '600px',
}));

const FilterChip = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ isActive, theme }) => ({
  flexShrink: 0,
  borderRadius: '100px',
  padding: '6px 16px',
  fontFamily: 'Satoshi',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '17px',
  whiteSpace: 'nowrap',
  color: isActive ? colors.white : theme.palette.invertedContent?.TETRIARY,
  border: `1px solid ${theme.palette.blueColors?.PLUS_2}`,
  boxShadow: isActive ? 'none' : `0px 0px 2px 1px ${theme.palette.blueColors?.PLUS_2}`,
  backgroundColor: isActive ? theme.palette.blueColors?.PLUS_1 : 'transparent',
  transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.blueColors?.PLUS_1,
    color: colors.white,
    boxShadow: 'none',
  },
}));

const ArrowButton = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  width: '32px',
  height: '32px',
  color: theme.palette.invertedContent?.TETRIARY,
  border: `1px solid ${theme.palette.blueColors?.PLUS_2}`,
  boxShadow: `0px 0px 2px 1px ${theme.palette.blueColors?.PLUS_2}`,
  borderRadius: '50%',
  padding: 0,
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.blueColors?.PLUS_1,
    color: colors.white,
    boxShadow: 'none',
  },
}));

const SCROLL_STEP = 200;

export const BlogPage = ({ blogPosts, categories }: BlogProps) => {
  const router = useRouter();
  const scrollTrackRef = useRef<HTMLDivElement>(null);

  const isSelected = (category: string) => {
    if (!router.query.slug) return false;
    return router.query.slug === normalizeCategoryName(category);
  };

  const handleRedirect = (category: string, isAll = false) => {
    if (isAll) {
      router.push('/blog');
      return;
    }
    router.push(`/blog/category/${normalizeCategoryName(category)}`);
  };

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollTrackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollTrackRef.current;
    if (!el) return;
    // rAF ensures layout is complete before measuring overflow
    const raf = requestAnimationFrame(updateScrollState);
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState]);

  const scrollLeft = useCallback(() => {
    if (scrollTrackRef.current)
      scrollTrackRef.current.scrollLeft -= SCROLL_STEP;
  }, []);

  const scrollRight = useCallback(() => {
    if (scrollTrackRef.current)
      scrollTrackRef.current.scrollLeft += SCROLL_STEP;
  }, []);

  const filteredBlogPosts = blogPosts.filter((post) => {
    if (!router.query.slug) return true;
    if (!post.category) return false;
    return post.category?.some((category) => {
      if (!category?.name) return false;
      return normalizeCategoryName(category?.name) === router.query.slug;
    });
  });

  return (
    <Layout blogPosts={blogPosts}>
      <NextHeadWithMeta {...blogSeo} />

      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Section width="normal">
          <Box sx={{ pt: 10, pb: 6 }}>
            <IntroHeading variant="h1">Blog</IntroHeading>
            <IntroSubtitle variant="body1">
              Technical insights, implementation guides, and industry deep dives
              from the Rumble Fish team.
            </IntroSubtitle>
          </Box>
        </Section>
      </Box>

      <Section width="normal">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            pb: 3,
          }}>
          {canScrollLeft && (
            <ArrowButton onClick={scrollLeft} aria-label="scroll left" disableRipple>
              ‹
            </ArrowButton>
          )}
          <Box
            ref={scrollTrackRef}
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              overflowX: 'auto',
              gap: 1,
              scrollBehavior: 'smooth',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}>
            <FilterChip
              isActive={!router.query.slug}
              onClick={() => handleRedirect('All', true)}>
              All
            </FilterChip>
            {categories.map((category) => (
              <FilterChip
                key={category}
                isActive={isSelected(category)}
                onClick={() => handleRedirect(category)}>
                {category}
              </FilterChip>
            ))}
          </Box>
          {canScrollRight && (
            <ArrowButton onClick={scrollRight} aria-label="scroll right" disableRipple>
              ›
            </ArrowButton>
          )}
        </Box>
      </Section>

      <Articles articles={filteredBlogPosts as unknown as Article[]} />
      <Contact color="blue" />
      <NextHeadWithMeta />
    </Layout>
  );
};
